// ═══════════════════════════════════════════════════════════════════════════════
// SESSION MANAGER — Persistance locale chiffrée légère
// Aucune dépendance externe. Chiffrement XOR + base64.
// Objectif : tampon local, pas entrepôt de production.
// ═══════════════════════════════════════════════════════════════════════════════

const STORAGE_KEY = "pesacore_sessions_v1";
const ENCRYPTION_KEY = "pesacore_local_key_v1";   // clé statique locale, pas un secret cryptographique

// ── Chiffrement XOR simple (obfuscation, pas sécurité forte) ─────────────────
// Empêche la lecture accidentelle dans localStorage.
// N'empêche PAS un attaquant déterminé.

const xorCipher = (input, key) => {
  if (!input) return "";
  let result = "";
  for (let i = 0; i < input.length; i++) {
    result += String.fromCharCode(
      input.charCodeAt(i) ^ key.charCodeAt(i % key.length)
    );
  }
  return result;
};

const encode = (obj) => {
  try {
    const json = JSON.stringify(obj);
    const cipher = xorCipher(json, ENCRYPTION_KEY);
    // btoa ne gère pas les caractères non-ASCII → on encode en UTF-8 d'abord
    return btoa(unescape(encodeURIComponent(cipher)));
  } catch (e) {
    console.error("[sessionManager] Erreur d'encodage :", e);
    return null;
  }
};

const decode = (encoded) => {
  try {
    const cipher = decodeURIComponent(escape(atob(encoded)));
    const json = xorCipher(cipher, ENCRYPTION_KEY);
    return JSON.parse(json);
  } catch (e) {
    console.error("[sessionManager] Erreur de décodage :", e);
    return null;
  }
};

// ── Lecture / écriture du store ──────────────────────────────────────────────

const readStore = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = decode(raw);
    return parsed || {};
  } catch (e) {
    console.error("[sessionManager] Erreur de lecture :", e);
    return {};
  }
};

const writeStore = (store) => {
  try {
    const encoded = encode(store);
    if (!encoded) return false;
    localStorage.setItem(STORAGE_KEY, encoded);
    return true;
  } catch (e) {
    console.error("[sessionManager] Erreur d'écriture :", e);
    return false;
  }
};

// ── API publique ─────────────────────────────────────────────────────────────

export const saveSession = (trace) => {
  if (!trace || !trace.session_id) {
    console.warn("[sessionManager] Trace invalide");
    return false;
  }

  const store = readStore();
  store[trace.session_id] = {
    ...trace,
    _saved_at: new Date().toISOString(),
  };
  return writeStore(store);
};

export const loadSession = (sessionId) => {
  if (!sessionId) return null;
  const store = readStore();
  return store[sessionId] || null;
};

export const listSessions = (filters = {}) => {
  const store = readStore();
  let sessions = Object.values(store);

  if (filters.actor_id) {
    sessions = sessions.filter(s => s.actor_id === filters.actor_id);
  }
  if (filters.referential) {
    sessions = sessions.filter(s => s.referential === filters.referential);
  }
  if (filters.domain) {
    sessions = sessions.filter(s => s.domain === filters.domain);
  }
  if (filters.certification_ready !== undefined) {
    sessions = sessions.filter(s => s.certification_ready === filters.certification_ready);
  }
  if (filters.since) {
    const since = new Date(filters.since).getTime();
    sessions = sessions.filter(s => new Date(s.timestamp_start).getTime() >= since);
  }

  // Tri par date de début décroissante
  sessions.sort((a, b) =>
    new Date(b.timestamp_start).getTime() - new Date(a.timestamp_start).getTime()
  );

  if (filters.limit) {
    sessions = sessions.slice(0, filters.limit);
  }

  return sessions;
};

export const deleteSession = (sessionId) => {
  if (!sessionId) return false;
  const store = readStore();
  if (!(sessionId in store)) return false;
  delete store[sessionId];
  return writeStore(store);
};

export const clearAll = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
    return true;
  } catch (e) {
    console.error("[sessionManager] Erreur de purge :", e);
    return false;
  }
};

// ── Statistiques ─────────────────────────────────────────────────────────────

export const getStats = () => {
  const sessions = listSessions();
  const total = sessions.length;
  const certified = sessions.filter(s => s.certification_ready).length;
  const byDomain = {};
  const byReferential = {};

  sessions.forEach(s => {
    if (s.domain) byDomain[s.domain] = (byDomain[s.domain] || 0) + 1;
    if (s.referential) byReferential[s.referential] = (byReferential[s.referential] || 0) + 1;
  });

  return { total, certified, byDomain, byReferential };
};

// ── Diagnostic ───────────────────────────────────────────────────────────────

export const getStorageInfo = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return {
      exists: !!raw,
      size_bytes: raw ? new Blob([raw]).size : 0,
      size_kb: raw ? Math.round(new Blob([raw]).size / 1024 * 100) / 100 : 0,
    };
  } catch (e) {
    return { exists: false, size_bytes: 0, size_kb: 0 };
  }
};